import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Building2, Plus, Edit, Trash2 } from 'lucide-react'
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
  useHrContext,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import type { Department } from '@/types/hr'

export default function DepartmentsPage() {
  const { data: hrCtx } = useHrContext()
  const isHrAdmin = hrCtx?.isHrAdmin ?? false
  const { toast } = useToast()
  const { data: departments, isLoading } = useDepartments()
  const createMutation = useCreateDepartment()
  const updateMutation = useUpdateDepartment()
  const deleteMutation = useDeleteDepartment()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Department | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Department | null>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [parentId, setParentId] = useState<string>('__none')

  const openForm = (d: Department | null) => {
    setEditing(d)
    setName(d?.name ?? '')
    setDescription(d?.description ?? '')
    setParentId(d?.parent_department_id ?? '__none')
    setFormOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' })
      return
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      parent_department_id: parentId === '__none' ? null : parentId,
    }
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: payload })
        toast({ title: 'Department updated' })
      } else {
        await createMutation.mutateAsync(payload)
        toast({ title: 'Department created' })
      }
      setFormOpen(false)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast({ title: 'Error', description: msg || 'Failed to save', variant: 'destructive' })
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteMutation.mutateAsync(confirmDelete.id)
      toast({ title: 'Department deleted' })
      setConfirmDelete(null)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast({
        title: 'Cannot delete',
        description: msg || 'Failed to delete',
        variant: 'destructive',
      })
    }
  }

  const list = departments ?? []
  const parentOptions = list.filter((d) => d.id !== editing?.id)

  return (
    <main className="container mx-auto p-6 max-w-5xl">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" aria-hidden="true" />
            Departments
          </h1>
          <p className="text-muted-foreground text-sm">
            Organizational units used to group employees
          </p>
        </div>
        {isHrAdmin && (
          <Button onClick={() => openForm(null)} aria-label="Create new department">
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            New Department
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">All Departments</CardTitle>
          <CardDescription>
            {list.length} {list.length === 1 ? 'department' : 'departments'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p role="status" aria-live="polite" className="text-muted-foreground">
              Loading…
            </p>
          ) : list.length === 0 ? (
            <p className="text-muted-foreground">No departments yet.</p>
          ) : (
            <ul className="divide-y">
              {list.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium">{d.name}</p>
                    {d.parent_name && (
                      <p className="text-xs text-muted-foreground">
                        Sub-department of {d.parent_name}
                      </p>
                    )}
                    {d.description && (
                      <p className="text-sm text-muted-foreground mt-1">{d.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className="text-sm text-muted-foreground"
                      aria-label={`${d.employee_count ?? 0} employees`}
                    >
                      {d.employee_count ?? 0} {d.employee_count === 1 ? 'employee' : 'employees'}
                    </span>
                    {isHrAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openForm(d)}
                        aria-label={`Edit ${d.name}`}
                      >
                        <Edit className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                    {isHrAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => setConfirmDelete(d)}
                        aria-label={`Delete ${d.name}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Department' : 'New Department'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div>
              <Label htmlFor="dept-name">Name *</Label>
              <Input id="dept-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="dept-desc">Description</Label>
              <Textarea
                id="dept-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="dept-parent">Parent department</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger id="dept-parent">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {parentOptions.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {editing ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This is a soft delete. Departments with assigned employees cannot be deleted —
              reassign those employees first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
